/** @format */

import * as React from 'react';
import * as PopoverPrimitive from '@radix-ui/react-popover';
import { cn } from '@clarix/ui';

const ARROW_SIZE = 12;

const arrowConfig = {
  right: {
    position: '-left-[11px]',
    path: `M${ARROW_SIZE} 0 L0 ${ARROW_SIZE} L${ARROW_SIZE} ${ARROW_SIZE * 2} Z`,
  },
  left: {
    position: '-right-[11px]',
    path: `M0 0 L${ARROW_SIZE} ${ARROW_SIZE} L0 ${ARROW_SIZE * 2} Z`,
  },
  bottom: {
    position: '-top-[11px]',
    path: `M0 ${ARROW_SIZE} L${ARROW_SIZE} 0 L${ARROW_SIZE * 2} ${ARROW_SIZE} Z`,
  },
  top: {
    position: '-bottom-[11px]',
    path: `M0 0 L${ARROW_SIZE} ${ARROW_SIZE} L${ARROW_SIZE * 2} 0 Z`,
  },
} as const;

type ArrowSide = keyof typeof arrowConfig;

function isHorizontal(side: ArrowSide) {
  return side === 'left' || side === 'right';
}

interface PopoverContentWithArrowProps extends React.ComponentPropsWithoutRef<
  typeof PopoverPrimitive.Content
> {
  arrowOffset?: number;
}

const PopoverContentWithArrow = React.forwardRef<
  React.ElementRef<typeof PopoverPrimitive.Content>,
  PopoverContentWithArrowProps
>(
  (
    {
      className,
      children,
      side = 'right',
      align = 'start',
      sideOffset = 12,
      arrowOffset = 20,
      ...props
    },
    ref,
  ) => {
    const arrowSide = (side ?? 'right') as ArrowSide;
    const config = arrowConfig[arrowSide];
    const horizontal = isHorizontal(arrowSide);

    const svgWidth = horizontal ? ARROW_SIZE : ARROW_SIZE * 2;
    const svgHeight = horizontal ? ARROW_SIZE * 2 : ARROW_SIZE;

    const arrowPositionStyle: React.CSSProperties = horizontal
      ? { top: arrowOffset, transform: 'translateY(-50%)' }
      : { left: arrowOffset, transform: 'translateX(-50%)' };

    return (
      <PopoverPrimitive.Portal>
        <PopoverPrimitive.Content
          ref={ref}
          side={side}
          align={align}
          sideOffset={sideOffset}
          className={cn(
            'bg-surface relative z-50 overflow-visible rounded-xl border-none p-0 shadow-none outline-none',
            '[filter:drop-shadow(0_4px_16px_rgba(0,0,0,0.12))]',
            'data-[state=open]:animate-in data-[state=closed]:animate-out',
            'data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0',
            'data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95',
            'data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2',
            'data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2',
            className,
          )}
          {...props}
        >
          <span
            className={cn('absolute', config.position)}
            style={{
              width: svgWidth,
              height: svgHeight,
              ...arrowPositionStyle,
            }}
            aria-hidden
          >
            <svg
              width={svgWidth}
              height={svgHeight}
              viewBox={`0 0 ${svgWidth} ${svgHeight}`}
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
              className="block"
            >
              <path d={config.path} fill="white" />
            </svg>
          </span>

          <div className="relative z-[2]">{children}</div>
        </PopoverPrimitive.Content>
      </PopoverPrimitive.Portal>
    );
  },
);
PopoverContentWithArrow.displayName = 'PopoverContentWithArrow';

export { PopoverContentWithArrow };
